import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { ChevronRight, ArrowRight, Phone, Gauge, Info, RotateCcw } from "lucide-react";
import { PHONE, PHONE_HREF } from "@/data/services";

/**
 * Water Pressure Test — simple bucket-and-stopwatch method.
 * User fills a 9L bucket at an outside tap and times how long it takes.
 * We convert to flow rate (L/min) and give a pressure range verdict.
 */

const classify = (seconds) => {
  const flowRate = 9 / (seconds / 60); // L/min
  if (seconds <= 0) return null;
  if (flowRate >= 50) {
    return {
      status: "high",
      label: "High pressure",
      color: "text-emergency",
      border: "border-emergency",
      bg: "bg-emergency/[0.04]",
      flow: flowRate,
      desc: "Pressure is higher than the Australian standard (500 kPa / 72 PSI). Over time this damages seals, hoses and appliances — your dishwasher and washing machine warranties can be voided above 500 kPa. A pressure-limiting valve (PLV) at the meter is usually the fix.",
      cta: "Book a PLV install",
    };
  }
  if (flowRate >= 20) {
    return {
      status: "ok",
      label: "Healthy pressure",
      color: "text-emerald-400",
      border: "border-emerald-500",
      bg: "bg-emerald-500/[0.04]",
      flow: flowRate,
      desc: "You're in the sweet spot. Enough pressure for a strong shower and appliance performance, without stressing seals or pipes.",
      cta: null,
    };
  }
  if (flowRate >= 10) {
    return {
      status: "low",
      label: "Low pressure",
      color: "text-gold",
      border: "border-gold/40",
      bg: "bg-gold/[0.04]",
      flow: flowRate,
      desc: "Your mains pressure or supply volume is on the low side — fine for most homes but noticeable in showers and when two taps run at once. Causes: partially closed stop valve, blocked PLV, undersized supply, or low street pressure.",
      cta: "Get it diagnosed",
    };
  }
  return {
    status: "very-low",
    label: "Very low pressure",
    color: "text-emergency",
    border: "border-emergency",
    bg: "bg-emergency/[0.04]",
    flow: flowRate,
    desc: "Flow is well below what you'd expect from a healthy home supply. Common causes: closed main stop tap, a leak drawing supply, a faulty PLV, or a partially blocked service line. Worth looking at properly.",
    cta: "Get it diagnosed",
  };
};

const PressureTest = () => {
  const [seconds, setSeconds] = useState("");
  const [result, setResult] = useState(null);

  const submit = (e) => {
    e.preventDefault();
    const n = parseFloat(seconds);
    if (!n || n <= 0) {
      alert("Enter a positive number of seconds.");
      return;
    }
    setResult(classify(n));
    setTimeout(() => {
      document.getElementById("pressure-result")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const reset = () => {
    setSeconds("");
    setResult(null);
  };

  return (
    <div data-testid="pressure-test-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gold-glow opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-page px-6 lg:px-14 pt-20 pb-16">
          <nav className="text-xs tracking-widerx uppercase flex items-center gap-2 mb-12">
            <Link to="/" className="text-muted hover:text-gold">Home</Link>
            <ChevronRight size={12} className="text-dim" />
            <Link to="/tools" className="text-muted hover:text-gold">Tools</Link>
            <ChevronRight size={12} className="text-dim" />
            <span className="text-ink">Pressure Test</span>
          </nav>
          <div className="max-w-3xl">
            <div className="eyebrow mb-6"><span>Free Tool · 60-second test</span></div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-display font-black text-[clamp(3rem,9vw,7.5rem)] leading-[0.9] text-ink"
            >
              Water pressure.
              <br />
              <span className="text-gold">In a bucket.</span>
            </motion.h1>
            <p className="mt-8 text-muted text-lg md:text-xl max-w-xl leading-relaxed">
              No gauge needed. Grab a 9-litre bucket and an outside tap. We&apos;ll tell you whether
              your mains pressure is healthy, low, or dangerously high (yes, it&apos;s a thing).
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16 border-t border-line bg-elevated">
        <div className="mx-auto max-w-page px-6 lg:px-14 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start max-w-3xl">
          <div className="eyebrow flex-shrink-0">How to test</div>
          <ol className="text-muted text-lg leading-relaxed space-y-3 list-decimal pl-5">
            <li>Grab a <strong className="text-ink">9-litre bucket</strong> (standard mop bucket) and go to an <strong className="text-ink">outside tap closest to the meter</strong>.</li>
            <li>Turn the tap <strong className="text-ink">fully on</strong>. Hold the bucket under it.</li>
            <li>Time how many <strong className="text-ink">seconds</strong> it takes to fill the bucket exactly.</li>
            <li>Enter that number below.</li>
          </ol>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-page px-6 lg:px-14">
          <form onSubmit={submit} className="bg-card border border-line p-8 md:p-12 max-w-2xl" data-testid="pressure-form">
            <div className="text-gold text-xs tracking-widerx uppercase mb-3">Your result</div>
            <h2 className="font-display text-3xl md:text-4xl text-ink mb-5">
              How long did it take?
            </h2>
            <div className="flex items-baseline gap-4 mb-6">
              <input
                type="number"
                step="0.1"
                min="0"
                value={seconds}
                onChange={(e) => setSeconds(e.target.value)}
                placeholder="12"
                data-testid="pressure-seconds"
                className="w-32 bg-transparent border-b border-line focus:border-gold text-ink text-4xl font-body font-medium py-3 outline-none tabular-nums"
              />
              <span className="text-muted text-sm tracking-widerx uppercase">seconds</span>
            </div>
            <button type="submit" className="btn-gold" data-testid="pressure-submit">
              Check pressure <ArrowRight size={16} />
            </button>
          </form>

          {result && (
            <motion.div
              id="pressure-result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={`mt-10 p-8 md:p-10 border-l-4 ${result.border} ${result.bg} max-w-2xl`}
              data-testid="pressure-result"
            >
              <div className="flex items-center gap-3 mb-4">
                <Gauge size={26} className={result.color} />
                <span className={`font-display text-2xl md:text-3xl uppercase ${result.color}`}>
                  {result.label}
                </span>
              </div>
              <div className="font-body font-medium text-3xl text-ink mb-1 tabular-nums">
                {result.flow.toFixed(1)} L/min
              </div>
              <div className="text-muted text-xs tracking-widerx uppercase mb-6">
                Approx flow rate at that tap
              </div>
              <p className="text-muted leading-relaxed max-w-xl">{result.desc}</p>
              {result.cta && (
                <div className="mt-6 flex flex-wrap gap-3">
                  <a href={PHONE_HREF} className="btn-gold" data-testid="pressure-cta-call">
                    <Phone size={16} /> {result.cta}
                  </a>
                  <button onClick={reset} className="btn-ghost" data-testid="pressure-reset">
                    <RotateCcw size={14} /> Test again
                  </button>
                </div>
              )}
              {!result.cta && (
                <div className="mt-6">
                  <button onClick={reset} className="btn-ghost" data-testid="pressure-reset">
                    <RotateCcw size={14} /> Test again
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>

      <section className="py-16 bg-elevated border-t border-line">
        <div className="mx-auto max-w-page px-6 lg:px-14">
          <div className="max-w-3xl p-6 border-l-2 border-gold/40 bg-gold/[0.03] flex items-start gap-3">
            <Info size={18} className="text-gold mt-0.5 flex-shrink-0" />
            <div className="text-muted text-sm leading-relaxed">
              <strong className="text-ink">About the maths:</strong> we convert bucket-fill time to flow rate (L/min),
              then approximate the pressure band. Flow rate is a proxy for pressure, assuming an
              unrestricted 15–20mm tap. For a real pressure reading in kPa, book a service call —
              we use a gauge direct to the mains and can confirm within ±5 kPa.
              <br/><br/>
              Australian Standard AS/NZS 3500 caps domestic pressure at <strong className="text-ink">500 kPa (~72 PSI)</strong>.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PressureTest;
